import useStore from '@/store';
import { ActivitySuggestion, isValidDate } from '@/store/activities';
import { CitySuggestion } from '@/store/cities';
import { useCities } from '@/store/hooks/cities';
import { useActivities } from '@/store/hooks/use-activities';
import {
  faCalendar,
  faFlag,
  faTag,
  faUser,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import { navigate } from 'gatsby';
import React from 'react';
import CitiesFilter from '../search/filters/cities-filter';
import PeriodsFilter from '../search/filters/periods';
import FiltersSubmitButton from '../search/filters/submit-button';
import TextFilter from '../search/filters/text-filter';

interface ActivitySearchQueryParams {
  search?: string;
  near?: [CitySuggestion['longitude'], CitySuggestion['latitude']];
  city?: string;
}

const ActivitiesFilters: React.FunctionComponent = () => {
  // Hooks
  const {
    suggestions,
    currentSuggestion,
    currentSearch,
    currentPeriod,
    actions: {
      find,
      findSuggestions,
      setCurrentSuggestion,
      setCurrentSearch,
      setCurrentPeriod,
    },
  } = useActivities();
  const { currentSuggestion: currentCitySuggestion } = useCities();
  const findArticles = useStore((state) => state.articles.find);

  // Effects
  const fetch = React.useCallback(() => {
    const query: ActivitySearchQueryParams = {};
    if (currentSuggestion) {
      query[currentSuggestion.field] = currentSuggestion.value;
    } else if (currentSearch) {
      query.search = currentSearch;
    }
    if (currentCitySuggestion) {
      query.near = [
        currentCitySuggestion.longitude,
        currentCitySuggestion.latitude,
      ];
      query.city = currentCitySuggestion.value;
    }
    if (
      currentPeriod /*  &&
      currentSuggestion &&
      currentSuggestion.field !== `category.title` &&
      currentSuggestion.field !== `title` */
    ) {
      query[`timing.dates[$elemMatch][$gte]`] = currentPeriod.$gte;
      query[`timing.dates[$elemMatch][$lte]`] = currentPeriod.$lte;
    }

    findArticles({
      search: currentSuggestion ? currentSuggestion.value : currentSearch,
      $limit: 3,
    });

    /* if (Object.keys(query)[0]) */ find(query);
  }, [currentSuggestion, currentCitySuggestion, currentSearch, currentPeriod]);

  React.useEffect(
    () => fetch(),
    [currentSuggestion, currentCitySuggestion, currentSearch, currentPeriod],
  );

  React.useEffect(() => {
    findSuggestions();
  }, []);

  const formRef = React.useRef<HTMLFormElement>();

  return (
    <div className="has-text-centered">
      <form
        ref={formRef}
        onSubmit={(e) => {
          formRef.current.reportValidity();
          e.preventDefault();
          navigate(`/agenda-des-activites-et-des-conferences/`);
        }}
      >
        <div className="columns">
          <div className="column">
            <TextFilter
              suggestions={suggestions || []}
              currentValue={currentSuggestion?.value || currentSearch}
              fetchSuggestions={(term) => findSuggestions({ term })}
              onSelect={setCurrentSuggestion}
              onEnterOrBlur={(value: string | ActivitySuggestion) => {
                if (typeof value === `string`) setCurrentSearch(value);
                else if (value.value) setCurrentSuggestion(value);
                else setCurrentSuggestion(undefined);
              }}
              placeholder="Nom, mot-clé..."
              itemIcons={{
                title: faCalendar,
                'category.title': faTag,
                'contact.name': faUser,
                'contact.organization': faUsers,
              }}
              color="secondary"
            />
          </div>
          <div className="column">
            <CitiesFilter
              defaultValue={
                currentCitySuggestion && currentCitySuggestion.value
              }
              color="secondary"
            />
          </div>
          <div className="column">
            <PeriodsFilter
              onChange={(value) => {
                const { $gte, $lte } = value;
                if (isValidDate($gte) && isValidDate($lte)) {
                  setCurrentPeriod(value);
                }
              }}
              defaultValue={currentPeriod?.key}
              inputDateDefaultValue={currentPeriod.inputDateValue}
              color="secondary"
            />
            <div className="has-text-center">
              {currentPeriod &&
                isValidDate(currentPeriod.$gte) &&
                isValidDate(currentPeriod.$lte) && (
                  <small>
                    Du {format(currentPeriod.$gte, `dd/MM/yyyy`)} au{` `}
                    {format(currentPeriod.$lte, `dd/MM/yyyy`)}
                  </small>
                )}
            </div>
          </div>
          <div className="column is-narrow">
            <FiltersSubmitButton onClick={fetch} color="secondary" />
          </div>
        </div>
      </form>
    </div>
  );
};

export default ActivitiesFilters;
